import { useSelector } from 'react-redux';
import { useStaggeredAnimation } from '@/features/client-onboarding/hooks/use-staggered-animation';

import {
  CarePhase,
  CarePhaseDef,
  carePhaseDefs,
  carePhaseDefList,
  isCarePhasePast,
  isNullOrUndefined,
} from '@shared';
import { selectClient } from '@/model';

export interface CarePhaseComputedData {
  value: CarePhase;
  phaseStartAge: number | null;
  phaseCareMonthsNeeded: number;
  selectedCareEnvironment: number | null;
  phaseInflatedProfessionalShareCost: number;
  phaseInflatedRoomAndBoardCost: number;
  isDurationCustom: boolean;
  isPast: boolean;
  timelineText: string;
  timelineTooltip: string;
}

export function useCarePhasesSlide() {
  const client = useSelector(selectClient)!;

  const {
    clientId,
    clientFirstName,
    intakeSurvey: { clientHasStartedLtc, clientCurrentCarePhase },
    allPhaseCosts: { allPhaseCareMonthsNeeded },
    phaseCosts,
  } = client;

  const ltcDurationYears = allPhaseCareMonthsNeeded / 12;

  // The timeline renders every phase, so the phase it is "on" is the client's
  // current one — phases before it are already behind them. The guard matches
  // the slide-sequence filter, so the phase named here always has a slide.
  const activeCarePhase =
    clientHasStartedLtc && clientCurrentCarePhase ? clientCurrentCarePhase : CarePhase.earlyCare;

  const carePhaseComputedData: CarePhaseComputedData[] = carePhaseDefList.map(phaseDef => {
    const data = getCarePhaseData(client, phaseDef.value);

    return {
      value: phaseDef.value,
      ...data,
      isPast: isCarePhasePast(phaseDef.value, client),
      timelineText: determineTimelineText(client, phaseDef.value, data.phaseStartAge),
      timelineTooltip: determineTimelineTooltip(client, phaseDef.value),
    };
  });

  const { visibleIndices, visibleLineIndices } = useStaggeredAnimation(
    carePhaseComputedData.length,
    { baseDelay: 150, cardDuration: 300, lineDuration: 150 },
  );

  return {
    client,
    clientId,
    clientFirstName,
    clientHasStartedLtc,
    clientCurrentCarePhase,
    ltcDurationYears,
    phaseCosts,
    activeCarePhase,
    carePhaseComputedData,
    visibleIndices,
    visibleLineIndices,
    nextButtonLabel: `Review ${carePhaseDefs[activeCarePhase].label}`,
  };
}

function getCarePhaseData(client: Client, carePhase: CarePhase) {
  const {
    intakeSurvey: { clientPhaseStartAges, clientBirthYear, clientCurrentCarePhase },
    phaseCosts,
    careEnvironmentSelections,
    clientPhasePredictedStartYears,
  } = client;

  const carePhaseIndex = carePhaseDefs[carePhase].index;
  const selectedCarePhaseCosts = phaseCosts[carePhaseIndex];
  const phaseCareMonthsNeeded = selectedCarePhaseCosts.phaseCareMonthsNeeded;
  const phaseInflatedProfessionalShareCost =
    selectedCarePhaseCosts.phaseInflatedProfessionalShareCost;
  const phaseInflatedRoomAndBoardCost =
    selectedCarePhaseCosts.phaseInflatedRoomAndBoardCost;
  const predictedPhaseStartYear = clientPhasePredictedStartYears[carePhase];
  const predictedPhaseStartAge = predictedPhaseStartYear
    ? predictedPhaseStartYear - clientBirthYear
    : clientPhaseStartAges[carePhase]!;

  const selectedCareEnvironment = careEnvironmentSelections[carePhase] ?? null;
  const phaseStartAge =
    clientCurrentCarePhase === carePhase ? clientPhaseStartAges[carePhase] : predictedPhaseStartAge;

  const isDurationCustom = selectedCarePhaseCosts.isDurationCustom;

  return {
    phaseStartAge,
    phaseCareMonthsNeeded,
    selectedCareEnvironment,
    phaseInflatedProfessionalShareCost,
    phaseInflatedRoomAndBoardCost,
    isDurationCustom,
  };
}

function findEntryByValue(value: CarePhase): CarePhaseDef | undefined {
  return carePhaseDefList.find(entry => entry.value === value);
}

function determineTimelineText(client: Client, carePhase: CarePhase, phaseStartAge: number | null) {
  const {
    intakeSurvey: { clientCurrentCarePhase },
  } = client;

  const phaseIndex = carePhaseDefs[carePhase].index;
  const phaseStartAgeText = phaseStartAge ? `${Math.round(phaseStartAge)}` : '';

  if (clientCurrentCarePhase === carePhase) {
    return `Since age ${phaseStartAgeText}`;
  }

  if (isNullOrUndefined(clientCurrentCarePhase)) {
    return `Expected age ${phaseStartAgeText}`;
  }

  const clientCurrentCarePhaseDef = findEntryByValue(clientCurrentCarePhase);

  if (clientCurrentCarePhaseDef!.index > phaseIndex) {
    return `Past`;
  }

  return `Expected age ${phaseStartAgeText}`;
}

function determineTimelineTooltip(client: Client, carePhase: CarePhase) {
  const {
    intakeSurvey: { clientPhaseStartAges, clientCurrentCarePhase, clientBirthYear },
    clientPhasePredictedStartYears,
  } = client;

  const phaseIndex = carePhaseDefs[carePhase].index;

  if (clientCurrentCarePhase === carePhase) {
    return `Since ${Math.round(clientPhaseStartAges[carePhase]! + clientBirthYear)}`;
  }

  if (isNullOrUndefined(clientCurrentCarePhase)) {
    return `Expected ${Math.round(clientPhasePredictedStartYears[carePhase]!)}`;
  }

  const clientCurrentCarePhaseDef = findEntryByValue(clientCurrentCarePhase);

  if (clientCurrentCarePhaseDef!.index > phaseIndex) {
    return `Past`;
  }

  return `Expected ${Math.round(clientPhasePredictedStartYears[carePhase]!)}`;
}
